/**
 * "What the cycle found" — the rules over the in-season run digest (bata-ui#148 step 4.3,
 * `docs/adr/9019`).
 *
 * Moved from the Cockpit in package A (2026-09-30, DEC-87): the Management pages
 * `/inseason/cycles/findings` (the list, by department or by store) and `/inseason/cycles/mix` (the
 * four tiles, issue 226) draw it now. The exit half is `exit-cycle-findings.ts`.
 *
 * `GET /inseason/runs/{run_id}/digest` (bata-platform ADR 6039) is ONE read that adds up a run's
 * stored rows. Everything here is arithmetic the page may do over what it publishes and nothing
 * more:
 *
 *   - a headline sentence per module, as a tested `defineHeadline` rule;
 *   - the chart specs, from the digest's own groups — never a figure the digest does not carry,
 *     and never a sum across currencies (money stays a list, one entry per currency);
 *   - the one fold the forms require: a stacked bar holds seven series, so the reason codes
 *     past the sixth fold into one "other" series whose units are the sum of theirs — the same
 *     unit, added, and the fold is named on the chart.
 *
 * Pure, so `cycle-findings.test.ts` pins every rule on strings and numbers.
 */

import type { InSeasonRunDigest, ProposalGroupDigest, StoreDigestRow } from '@/api/generated';
import { formatCompactNumber, formatPercent } from '@/lib/format';
import { defineHeadline } from '@/planning';
import type { ChartSpec, Datum } from '@/charts';

/** How many stores each of the digest's rankings lists — the platform's own default. */
export const DIGEST_TOP = 10;

/** A stacked bar's series cap (G2 §2.5): six named reason codes and one "other". */
export const REASON_SERIES = 6;

/** The reason key the digest publishes when a fill rate has nothing to divide by. */
export const NOTHING_REQUESTED = 'planning.inseason.digest.nothing_requested';

// ── the headlines ────────────────────────────────────────────────────────────

export const inseasonFoundHeadline = defineHeadline({
  keys: {
    nothing: 'inseason.found.inseason.headline.nothing',
    filled: 'inseason.found.inseason.headline.filled',
    noRequest: 'inseason.found.inseason.headline.noRequest',
  },
  choose: (digest: InSeasonRunDigest, { locale }) => {
    const book = digest.proposals;
    if (book.proposals === 0) return { variant: 'nothing' };
    const counts = {
      units: formatCompactNumber(book.units, locale).text,
      // The stores served: the markers' stores are not (bata-platform ADR 6183). A digest older than the split says all.
      stores: book.served_doors ?? book.stores,
      articles: book.served_articles ?? book.articles,
    };
    const rate = book.fill_rate.value;
    if (rate === null) return { variant: 'noRequest', values: counts };
    return {
      variant: 'filled',
      values: { ...counts, fill: formatPercent(rate, locale, 0), unmet: formatCompactNumber(book.unmet_units, locale).text },
    };
  },
});

/**
 * «Composizione» (`/inseason/cycles/mix`, issue 226): what the run wrote, by kind — its proposals,
 * its transfers and its price steps, each as the digest counts it.
 */
export const inseasonMixHeadline = defineHeadline({
  keys: {
    nothing: 'inseason.cycles.mix.headline.nothing',
    wrote: 'inseason.cycles.mix.headline.wrote',
  },
  choose: (digest: InSeasonRunDigest) => {
    const proposals = digest.proposals.served_lines ?? digest.proposals.proposals;
    const transfers = digest.transfers.transfers;
    const steps = digest.price_steps.price_steps;
    if (proposals === 0 && transfers === 0 && steps === 0) return { variant: 'nothing' };
    return { variant: 'wrote', values: { proposals, transfers, steps } };
  },
});

// ── the chart specs ──────────────────────────────────────────────────────────

/** A category label for a group key the translator cannot know — the key itself, or "unclassified". */
export type Label = (key: string) => string;

/**
 * Units by department family — a single-series bar, one bar per family, most units first (the
 * digest's order), with the unclassified lines as a bar of their own, named, never dropped.
 * `null` when the digest publishes no families (`groups` null: the scheme is not pinned or was
 * refused) or no family holds a line — the caller states that absence instead of a chart.
 */
export function familySpec(
  digest: InSeasonRunDigest, unclassifiedLabel: string,
  /** The platform's label of a family code (`useFamilyLabel`); absent, the code. Kept unique like any ordinal axis. */
  familyLabel?: Label,
): ChartSpec | null {
  const breakdown = digest.proposals.by_department_family;
  if (breakdown.groups === null) return null;
  const groups: readonly ProposalGroupDigest[] = [
    ...breakdown.groups,
    ...(breakdown.unclassified === null || breakdown.unclassified === undefined
      ? [] : [{ ...breakdown.unclassified, key: unclassifiedLabel }]),
  ];
  if (groups.length === 0) return null;
  const keys = groups.map((group) => group.key);
  const label = familyLabel === undefined ? (key: string): string => key : uniqueLabels(keys, (key) => (key === unclassifiedLabel ? key : familyLabel(key)));
  return {
    form: 'bar',
    orientation: 'horizontal',
    actionTitleKey: 'inseason.found.inseason.familyTitle',
    actionTitleValues: { families: breakdown.groups.length },
    perimeterKey: 'inseason.found.inseason.familyPerimeter',
    valueAxis: { unitKey: 'inseason.unit.units', zeroBased: true },
    categoryAxis: { kind: 'ordinal', labelKey: 'inseason.found.inseason.familyAxis', order: keys.map(label) },
    series: [{
      id: 'units', nameKey: 'inseason.found.inseason.unitsSeries',
      data: groups.map((group) => group.units), distinguisher: 'pattern-solid', slot: 1,
    }],
    tooltipKey: 'inseason.found.inseason.familyTooltip',
  };
}

/** The reason codes' shares of proposed and of unmet units, folded to `REASON_SERIES` + other. */
export interface ReasonMix {
  readonly codes: readonly string[];
  readonly folded: readonly string[];
  readonly units: readonly number[];
  readonly unmet: readonly number[];
}

export function reasonMix(groups: readonly ProposalGroupDigest[], keep = REASON_SERIES): ReasonMix {
  // Most units first; ties by code, so the order (and the colours, by id) are stable.
  const sorted = [...groups].sort((a, b) => b.units - a.units || b.unmet_units - a.unmet_units || a.key.localeCompare(b.key));
  const fold = sorted.length > keep + 1;
  const shown = fold ? sorted.slice(0, keep) : sorted;
  const rest = fold ? sorted.slice(keep) : [];
  return {
    codes: shown.map((group) => group.key),
    folded: rest.map((group) => group.key),
    units: [...shown.map((group) => group.units), ...(fold ? [rest.reduce((sum, group) => sum + group.units, 0)] : [])],
    unmet: [...shown.map((group) => group.unmet_units), ...(fold ? [rest.reduce((sum, group) => sum + group.unmet_units, 0)] : [])],
  };
}

/** The series id of the folded remainder. */
export const OTHER_REASONS = '__other__';

const REASON_DISTINGUISHERS = [
  'pattern-solid', 'pattern-hatch', 'pattern-dot', 'marker-circle', 'marker-square', 'marker-triangle', 'marker-diamond',
] as const;

/**
 * The reason-code mix — a stacked bar over two categories in ONE unit (pairs): what the run
 * proposed, and what it left unmet, each stacked by the reason code that decided the line.
 * `null` when the run has no line.
 */
export function reasonMixSpec(digest: InSeasonRunDigest, label: Label, labels: {
  readonly proposed: string;
  readonly unmet: string;
}): ChartSpec | null {
  const mix = reasonMix(digest.proposals.by_reason_code);
  if (mix.codes.length === 0) return null;
  const ids = [...mix.codes, ...(mix.folded.length > 0 ? [OTHER_REASONS] : [])];
  return {
    form: 'stacked-bar',
    orientation: 'horizontal',
    actionTitleKey: 'inseason.found.inseason.reasonTitle',
    actionTitleValues: { reasons: mix.codes.length + mix.folded.length },
    perimeterKey: 'inseason.found.inseason.reasonPerimeter',
    valueAxis: { unitKey: 'inseason.unit.units', zeroBased: true },
    categoryAxis: { kind: 'ordinal', labelKey: 'inseason.found.inseason.reasonAxis', order: [labels.proposed, labels.unmet] },
    series: ids.map((id, index) => ({
      id,
      nameKey: id === OTHER_REASONS ? 'inseason.found.inseason.otherReasons' : 'inseason.found.inseason.reasonSeries',
      nameValues: id === OTHER_REASONS ? { count: mix.folded.length } : { reason: label(id) },
      data: [mix.units[index] ?? 0, mix.unmet[index] ?? 0] as readonly Datum[],
      distinguisher: REASON_DISTINGUISHERS[index] ?? 'direct-label',
      slot: ((index % 7) + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7,
    })),
  };
}

/**
 * A label per key that stays unique: a key whose label collides with another's (two codes this
 * build names alike, or a code equal to a label) keeps its raw code. An ordinal axis refuses
 * duplicates, and two bars with one name could not be told apart anyway.
 */
export function uniqueLabels(keys: readonly string[], label: Label): Label {
  const labels = keys.map(label);
  return (key: string): string => {
    const index = keys.indexOf(key);
    const text = labels[index] ?? key;
    return labels.filter((candidate) => candidate === text).length > 1 ? key : text;
  };
}

// ── the department families as a way in (bata-ui#166 W4-7, `docs/adr/9100`) ──

/**
 * One department family's row in the Cockpit's family table. `family` is the key the proposals
 * read's `department_family` filter takes, or `null` for the unclassified lines — which no
 * filter can select, so that row is stated and never linked.
 */
export interface FamilyRow {
  readonly id: string;
  readonly family: string | null;
  readonly proposals: number;
  readonly units: number;
  readonly requestedUnits: number;
  readonly unmetUnits: number;
  readonly fillRate: number | null;
  /** The part of `requestedUnits` that is dropped-door need (ADR 6183); absent on a digest older than the split. */
  readonly droppedUnits?: number;
}

/** The digest's families in its own order, then the unclassified bucket; `null` with no groups. */
export function familyRows(digest: InSeasonRunDigest): readonly FamilyRow[] | null {
  const breakdown = digest.proposals.by_department_family;
  if (breakdown.groups === null) return null;
  const row = (group: ProposalGroupDigest, family: string | null): FamilyRow => ({
    id: family ?? UNCLASSIFIED_FAMILY_ROW,
    family,
    proposals: group.proposals,
    units: group.units,
    requestedUnits: group.requested_units,
    unmetUnits: group.unmet_units,
    fillRate: group.fill_rate.value ?? null,
    ...(group.dropped_door_requested_units === undefined ? {} : { droppedUnits: group.dropped_door_requested_units }),
  });
  const unclassified = breakdown.unclassified ?? null;
  return [
    ...breakdown.groups.map((group) => row(group, group.key)),
    ...(unclassified === null ? [] : [row(unclassified, null)]),
  ];
}

/**
 * One store of the digest's two rankings, as «Raggruppa per: Negozio» draws it (issue 226). The
 * figures are the store's own, whichever ranking listed it.
 */
export interface StoreRow {
  readonly storeKey: string;
  readonly proposals: number;
  readonly units: number;
  readonly requestedUnits: number;
  readonly unmetUnits: number;
  /** The part of `requestedUnits` that is dropped-door need (ADR 6183); absent on a digest older than the split. */
  readonly droppedUnits?: number;
}

/**
 * The two rankings — most units proposed, most unmet need — merged into one list, each store once,
 * most units first, then most unmet, then by code (a stable order the planner's sort replaces).
 * The digest publishes only the top `digest.top` of each, so this is never every store of the run;
 * the page says so. Pure.
 */
export function storeRows(digest: InSeasonRunDigest): readonly StoreRow[] {
  const seen = new Map<string, StoreRow>();
  const add = (row: StoreDigestRow): void => {
    if (seen.has(row.store_key)) return;
    seen.set(row.store_key, {
      storeKey: row.store_key, proposals: row.proposals, units: row.units,
      requestedUnits: row.requested_units, unmetUnits: row.unmet_units,
      ...(row.dropped_door_requested_units === undefined ? {} : { droppedUnits: row.dropped_door_requested_units }),
    });
  };
  digest.proposals.top_stores_by_units.forEach(add);
  digest.proposals.top_stores_by_unmet_need.forEach(add);
  return [...seen.values()].sort((a, b) => b.units - a.units || b.unmetUnits - a.unmetUnits || a.storeKey.localeCompare(b.storeKey));
}

/** The row id of the unclassified bucket — not a family key, so it cannot collide with one. */
export const UNCLASSIFIED_FAMILY_ROW = '\u0000unclassified';

// ── what else the digest publishes (bata-ui#166 W4-7, `docs/adr/9100`) ─────

/**
 * Price steps by depth band — a bar, one per band in ascending depth, labelled by its bounds.
 * `null` when the run priced nothing. Read from the PRICE run's digest (F16): the cycle run on
 * screen may be a replenishment run that priced nothing.
 */
export function depthBandSpec(digest: InSeasonRunDigest, bandLabel: (lower: number, upper: number) => string): ChartSpec | null {
  const bands = [...digest.price_steps.by_depth_band].sort((a, b) => a.lower_points - b.lower_points);
  if (bands.length === 0) return null;
  return {
    form: 'bar',
    orientation: 'vertical',
    actionTitleKey: 'inseason.found.inseason.depthTitle',
    actionTitleValues: { steps: digest.price_steps.price_steps, bands: bands.length },
    perimeterKey: 'inseason.found.inseason.depthPerimeter',
    valueAxis: { unitKey: 'inseason.unit.priceSteps', zeroBased: true },
    categoryAxis: { kind: 'ordinal', labelKey: 'inseason.found.inseason.depthAxis', order: bands.map((band) => bandLabel(band.lower_points, band.upper_points)) },
    series: [{
      id: 'steps', nameKey: 'inseason.found.inseason.depthSeries',
      data: bands.map((band) => band.price_steps), distinguisher: 'pattern-solid', slot: 3,
    }],
  };
}

/** Transfer units by purpose — a bar, one per purpose, most units first. `null` with no transfer. */
export function transferPurposeSpec(digest: InSeasonRunDigest, label: Label): ChartSpec | null {
  const purposes = [...digest.transfers.by_purpose].sort((a, b) => b.units - a.units || a.purpose.localeCompare(b.purpose));
  if (purposes.length === 0) return null;
  return {
    form: 'bar',
    orientation: 'horizontal',
    actionTitleKey: 'inseason.found.inseason.purposeTitle',
    actionTitleValues: { transfers: digest.transfers.transfers, purposes: purposes.length },
    perimeterKey: 'inseason.found.inseason.purposePerimeter',
    valueAxis: { unitKey: 'inseason.unit.units', zeroBased: true },
    categoryAxis: { kind: 'ordinal', labelKey: 'inseason.found.inseason.purposeAxis', order: purposes.map((row) => label(row.purpose)) },
    series: [{
      id: 'units', nameKey: 'inseason.found.inseason.purposeSeries',
      data: purposes.map((row) => row.units), distinguisher: 'pattern-solid', slot: 4,
    }],
  };
}
